"use client";

import { useState, useCallback, useMemo } from "react";
import { validateEmail } from "../validation";
import type { CheckoutStep, Guest } from "../utils/types";

export type UseCheckoutGiftCardProps = {
  product: any;
  school?: any;
  user?: any;
  initialAmount?: number;
  /** When true, shows the giftcard-details step first (for webcomponents/widget) */
  showGiftcardDetailsStep?: boolean;
  /** Initial values for giftcard details (pre-filled) */
  initialGiftcardDetails?: {
    recipientEmail?: string;
    recipientName?: string;
    senderName?: string;
    message?: string;
  };
};

const defaultGuest: Guest = {
  firstName: "",
  lastName: "",
  email: "",
  phoneNumber: "",
  marketingOptIn: false,
  guest: false,
};

export function useCheckoutGiftCard({
  product,
  school,
  user,
  initialAmount = 5000, // Default £50.00
  showGiftcardDetailsStep = false,
  initialGiftcardDetails,
}: UseCheckoutGiftCardProps) {
  // Gift card specific state
  const [amount, setAmount] = useState(initialAmount);
  const [customAmount, setCustomAmount] = useState("");
  const [deliveryMethod, setDeliveryMethod] = useState<"email" | "post">(
    "email",
  );
  const [recipientEmail, setRecipientEmail] = useState(initialGiftcardDetails?.recipientEmail || "");
  const [recipientName, setRecipientName] = useState(initialGiftcardDetails?.recipientName || "");
  const [senderName, setSenderName] = useState(initialGiftcardDetails?.senderName || "");
  const [message, setMessage] = useState(initialGiftcardDetails?.message || "");

  // Legacy aliases for backwards compatibility
  const to = recipientName;
  const from = senderName;
  const setTo = setRecipientName;
  const setFrom = setSenderName;

  // Personal details
  const initialGuest = useMemo((): Guest => {
    if (user?.profile) {
      return {
        ...defaultGuest,
        firstName: user.profile.firstName || "",
        lastName: user.profile.lastName || "",
        email: user.emails?.[0]?.address || "",
        phoneNumber: user.profile.phone || "",
      };
    }
    return { ...defaultGuest };
  }, [user]);

  const [personalDetails, setPersonalDetails] = useState<Guest>(initialGuest);

  // Step management - optionally includes giftcard-details step
  const [step, setStep] = useState<CheckoutStep>(
    showGiftcardDetailsStep ? "giftcard-details" : "personal"
  );

  const visibleSteps: CheckoutStep[] = useMemo(() => {
    const baseSteps: CheckoutStep[] = showGiftcardDetailsStep
      ? ["giftcard-details", "personal"]
      : ["personal"];

    baseSteps.push("basketChoice");

    return baseSteps;
  }, [showGiftcardDetailsStep]);

  const currentStepIndex = visibleSteps.indexOf(step);
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === visibleSteps.length - 1;
  const isFree = amount === 0;

  const goToNextStep = useCallback(() => {
    const nextIndex = currentStepIndex + 1;
    if (nextIndex < visibleSteps.length) {
      setStep(visibleSteps[nextIndex]!);
    }
  }, [currentStepIndex, visibleSteps]);

  const goToPreviousStep = useCallback(() => {
    const prevIndex = currentStepIndex - 1;
    if (prevIndex >= 0) {
      setStep(visibleSteps[prevIndex]!);
    }
  }, [currentStepIndex, visibleSteps]);

  // Update personal details
  const updatePersonalDetails = useCallback(
    (field: keyof Guest, value: string | boolean) => {
      setPersonalDetails((prev) => ({ ...prev, [field]: value }));
    },
    [],
  );

  // Validation
  const canContinue = useCallback((): boolean => {
    switch (step) {
      case "giftcard-details": {
        // Validate giftcard details
        return (
          amount >= 500 && // Minimum £5
          recipientName.trim() !== "" &&
          senderName.trim() !== "" &&
          validateEmail(recipientEmail).isValid
        );
      }
      case "personal": {
        return (
          personalDetails.firstName.trim() !== "" &&
          personalDetails.lastName.trim() !== "" &&
          validateEmail(personalDetails.email).isValid
        );
      }
      case "basketChoice":
        return true;
      default:
        return false;
    }
  }, [step, personalDetails, amount, recipientName, senderName, recipientEmail]);

  // Loading states
  const [isLoading, setIsLoading] = useState(false);
  const [isConfirmingFreeBooking, setIsConfirmingFreeBooking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get booking data for API
  const getBookingData = useCallback(() => {
    return {
      firstName: personalDetails.firstName,
      lastName: personalDetails.lastName,
      phoneNumber: personalDetails.phoneNumber || "",
      email: personalDetails.email,
    };
  }, [personalDetails]);

  // Selected image state
  const [selectedImage, setSelectedImage] = useState<string>("");

  // Get full gift card data
  const getGiftCardData = useCallback(() => {
    return {
      amount,
      deliveryMethod,
      to: recipientName,
      from: senderName,
      message,
      recipientEmail,
      recipientName,
      senderName,
      selectedImage,
      ...getBookingData(),
    };
  }, [amount, deliveryMethod, recipientName, senderName, message, recipientEmail, selectedImage, getBookingData]);

  // Get giftcard details values for the step component
  const giftcardDetailsValues = useMemo(() => ({
    amount,
    customAmount,
    recipientName,
    senderName,
    message,
    recipientEmail,
    selectedImage,
  }), [amount, customAmount, recipientName, senderName, message, recipientEmail, selectedImage]);

  // Update giftcard details from step component
  const updateGiftcardDetails = useCallback((
    field: keyof typeof giftcardDetailsValues,
    value: string | number
  ) => {
    switch (field) {
      case "amount":
        setAmount(value as number);
        break;
      case "customAmount":
        setCustomAmount(value as string);
        break;
      case "recipientName":
        setRecipientName(value as string);
        break;
      case "senderName":
        setSenderName(value as string);
        break;
      case "message":
        setMessage(value as string);
        break;
      case "recipientEmail":
        setRecipientEmail(value as string);
        break;
      case "selectedImage":
        setSelectedImage(value as string);
        break;
    }
  }, []);

  return {
    // State
    step,
    amount,
    customAmount,
    deliveryMethod,
    to,
    from,
    message,
    recipientEmail,
    recipientName,
    senderName,
    selectedImage,
    personalDetails,
    error,
    isLoading,
    isConfirmingFreeBooking,
    giftcardDetailsValues,

    // Computed
    total: amount,
    isFree,
    visibleSteps,
    currentStepIndex,
    isFirstStep,
    isLastStep,
    product,
    school,
    showGiftcardDetailsStep,

    // Actions
    setAmount,
    setCustomAmount,
    setDeliveryMethod,
    setTo,
    setFrom,
    setMessage,
    setRecipientEmail,
    setRecipientName,
    setSenderName,
    setSelectedImage,
    goToNextStep,
    goToPreviousStep,
    updatePersonalDetails,
    updateGiftcardDetails,
    canContinue,
    getBookingData,
    getGiftCardData,
    setIsLoading,
    setIsConfirmingFreeBooking,
    setError,
  };
}
